import { useState, useCallback } from 'react';
import { useBitcoinPrice, usdToSats } from '@/hooks/useBitcoinPrice';
import { Bitcoin, RefreshCw, Zap, DollarSign, TrendingUp } from 'lucide-react';

function formatSats(sats: number): string {
  if (sats >= 1_000_000) {
    return sats.toLocaleString();
  }
  return sats.toLocaleString();
}

function formatBtc(sats: number): string {
  const btc = sats / 100_000_000;
  return btc.toFixed(8);
}

const QUICK_AMOUNTS = [1, 5, 10, 25, 50, 100];

export function SatsConverter() {
  const [inputValue, setInputValue] = useState('');
  const { data: price, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useBitcoinPrice();

  const dollarAmount = parseFloat(inputValue) || 0;
  const satsAmount = price ? usdToSats(dollarAmount, price.USD) : 0;
  const hasResult = dollarAmount > 0 && price;

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow digits and a single decimal point, max 2 decimal places
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
      setInputValue(val);
    }
  }, []);

  const handleQuickAmount = useCallback((amount: number) => {
    setInputValue(String(amount));
  }, []);

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="border-b border-primary/40 bg-black/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-full p-1.5" style={{ backgroundColor: '#F7931A' }}>
              <Bitcoin className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-lg tracking-tight text-white">
              Sats Converter
            </span>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh price"
            className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/70 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-2 py-1"
            aria-label="Refresh Bitcoin price"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg space-y-6">

          {/* Hero text */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              <span className="text-green-500">$</span> to{' '}
              <span className="text-primary">⚡</span> Sats
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              Instantly convert US dollars to Bitcoin satoshis
            </p>
            <ul className="text-muted-foreground/80 text-sm space-y-1 mt-2">
              <li>• Enter Your Cost In Dollars</li>
              <li>• The dollar amount will be converted to{' '}
                <a
                  href="https://strike.me/en/learn/what-are-satoshis/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/70 underline underline-offset-2 transition-colors"
                >
                  sats
                </a>
                {' '}below
              </li>
            </ul>
          </div>

          {/* BTC Price Banner */}
          <div className="flex items-center justify-center gap-2 text-sm">
            <TrendingUp className="w-4 h-4 text-primary flex-shrink-0" />
            {isLoading && (
              <span className="text-muted-foreground animate-pulse">Fetching BTC price…</span>
            )}
            {isError && (
              <span className="text-red-400">Failed to load price. <button onClick={() => refetch()} className="underline">Retry</button></span>
            )}
            {price && (
              <span className="text-foreground/80">
                1 BTC ={' '}
                <span className="text-white font-semibold">
                  ${price.USD.toLocaleString()}
                </span>
                {lastUpdated && (
                  <span className="text-muted-foreground ml-2">· {lastUpdated}</span>
                )}
              </span>
            )}
          </div>

          {/* Input card */}
          <div className="bg-zinc-900 border border-primary/50 rounded-2xl p-6 shadow-xl shadow-primary/10">
            <label htmlFor="usd-input" className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              Amount in US Dollars
            </label>
            <div className="relative flex items-center">
              <div className="absolute left-4 pointer-events-none">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <input
                id="usd-input"
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                placeholder="0.00"
                value={inputValue}
                onChange={handleInput}
                className="w-full bg-black border border-primary/40 focus:border-primary rounded-xl pl-11 pr-4 py-4 text-2xl font-bold text-white placeholder:text-muted-foreground/50 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50"
                aria-label="Dollar amount"
                autoFocus
              />
            </div>

            {/* Quick amount buttons */}
            <div className="mt-4">
              <p className="text-xs text-muted-foreground/70 mb-2">Quick amounts</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {QUICK_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleQuickAmount(amount)}
                    className={`py-2 rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      dollarAmount === amount
                        ? 'bg-white text-primary'
                        : 'border border-primary/20 bg-zinc-900 text-muted-foreground hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Result card */}
          <div
            className={`rounded-2xl p-6 border transition-all duration-300 ${
              hasResult
                ? 'bg-gradient-to-br from-primary/25 to-zinc-900 border-primary/60 shadow-xl shadow-primary/20'
                : 'bg-zinc-900/50 border-primary/20'
            }`}
            aria-live="polite"
          >
            {isLoading ? (
              <div className="text-center py-4 space-y-3">
                <div className="h-10 bg-zinc-800 rounded-xl animate-pulse w-2/3 mx-auto" />
                <div className="h-5 bg-zinc-800 rounded-lg animate-pulse w-1/2 mx-auto" />
              </div>
            ) : hasResult ? (
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Satoshis
                  </span>
                </div>
                <p className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: '#F7931A' }}>
                  {formatSats(satsAmount)}
                  <span className="text-2xl sm:text-3xl ml-2">sats</span>
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  ≈ {formatBtc(satsAmount)} BTC
                </p>
                <div className="mt-4 pt-4 border-t border-primary/20 flex flex-col sm:flex-row gap-2 justify-center text-xs text-muted-foreground">
                  <span>${dollarAmount.toFixed(2)} USD</span>
                  <span className="hidden sm:inline">·</span>
                  <span>@ ${price?.USD.toLocaleString()} / BTC</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Zap className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-muted-foreground/70 text-sm">
                  Enter a dollar amount above to see the sats equivalent
                </p>
              </div>
            )}
          </div>

          {/* Info blurb */}
          <div className="text-center text-xs text-muted-foreground/70 space-y-1">
            <p>1 Bitcoin = 100,000,000 satoshis · Price from mempool.space</p>
            <p>Auto-refreshes every 30 seconds</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-primary/20 py-4 px-4 text-center text-xs text-muted-foreground/60">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-primary transition-colors"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </div>
  );
}
